import { useCallback, useEffect, useRef, useState } from 'react';

// ─────────────────────────────────────────────────────────────────────
// FEEDBACK WHERE THE USER IS ALREADY LOOKING (#369).
//
// Found in the #216 Banepad rehearsal: an operator submitted a result, the
// confirmation appeared in a banner elsewhere on the screen, and they never saw
// it — because their eyes were on the button they had just pressed, waiting for
// it to change. The banner was working perfectly and was effectively invisible.
//
// The defect is not «missing notifications». It is that feedback arrived
// somewhere other than the point of action. A toast may SUPPLEMENT that; it
// cannot be the only signal for something the user just triggered.
//
// So this owns the three states a mutation has, in one place, next to the
// control:
//
//   pending   the command is in flight — the control says so and refuses to be
//             pressed again, which is also the double-submit guard
//   done      it worked, briefly, so the change is visible as a CHANGE rather
//             than only as a new resting state
//   error     it failed, and the message belongs here rather than in a banner
//             the user is not looking at
//
// ── WHAT THIS DELIBERATELY DOES NOT DO ──────────────────────────────
//
// It renders nothing. #369 is explicit that a universal visual component must
// not be forced where the natural product transition already reads better — a
// modal that closes, a row that appears, a score that changes. Those are
// feedback, and a spinner bolted on top of them is worse. This supplies the
// state; each surface decides what that looks like.
// ─────────────────────────────────────────────────────────────────────

export type ActionPhase = 'idle' | 'pending' | 'done' | 'error';

export interface AsyncAction<A extends unknown[]> {
  /** Run it. A second call while pending is ignored, not queued. */
  run: (...args: A) => Promise<void>;
  phase: ActionPhase;
  /** The failure, for showing next to the control. Null unless `phase` is 'error'. */
  error: string | null;
  /** Back to idle — for a form that reopens, or a dismissed error. */
  reset: () => void;
  /** `phase === 'pending'`, for a `disabled=` that reads plainly. */
  pending: boolean;
}

export interface AsyncActionOptions {
  /**
   * How long `done` lasts before falling back to idle.
   *
   * A confirmation that never clears becomes part of the furniture and stops
   * being read; one that vanishes in 200ms is never seen at all. Zero keeps
   * `done` until something else resets it, for a control whose success IS the
   * new resting state.
   */
  doneMs?: number;
  /** Turns a rejection into the sentence shown at the control. */
  messageFor?: (error: unknown) => string;
}

const DEFAULT_DONE_MS = 2000;

/**
 * Wrap a mutation so the control that triggers it can show its own state.
 *
 * ```tsx
 * const save = useAsyncAction(persistSettings);
 * <button onClick={() => save.run(next)} disabled={save.pending}>
 *   {save.phase === 'pending' ? t('common.saving') : t('common.save')}
 * </button>
 * {save.error && <p role="alert">{save.error}</p>}
 * ```
 */
export function useAsyncAction<A extends unknown[]>(
  fn: (...args: A) => unknown | Promise<unknown>,
  options: AsyncActionOptions = {},
): AsyncAction<A> {
  const { doneMs = DEFAULT_DONE_MS, messageFor } = options;
  const [phase, setPhase] = useState<ActionPhase>('idle');
  const [error, setError] = useState<string | null>(null);

  // Whether this component is still on screen. A mutation that outlives its
  // modal would otherwise set state on nothing — and, worse, the `pending`
  // guard below would be read from a component that no longer exists.
  const alive = useRef(true);
  const inFlight = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  // THE LATEST FUNCTION, WITHOUT RESUBSCRIBING. Call sites pass an inline
  // closure, so a dependency on `fn` would give `run` a new identity every
  // render — and any `useEffect` or memo keyed on it would loop.
  const latest = useRef(fn);
  latest.current = fn;

  const reset = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    setPhase('idle');
    setError(null);
  }, []);

  const run = useCallback(
    async (...args: A) => {
      // THE DOUBLE-SUBMIT GUARD, and it is a ref rather than `phase` on
      // purpose: two clicks in the same tick both read the same stale state, so
      // a check against the rendered value lets the second one through. That is
      // exactly the case this exists to stop — two bookings, one intent.
      if (inFlight.current) return;
      inFlight.current = true;
      if (timer.current) clearTimeout(timer.current);
      setPhase('pending');
      setError(null);
      try {
        await latest.current(...args);
        if (!alive.current) return;
        setPhase('done');
        if (doneMs > 0) {
          timer.current = setTimeout(() => {
            if (alive.current) setPhase('idle');
          }, doneMs);
        }
      } catch (e) {
        if (!alive.current) return;
        setPhase('error');
        setError(
          messageFor
            ? messageFor(e)
            : e instanceof Error && e.message
              ? e.message
              : String(e ?? ''),
        );
      } finally {
        inFlight.current = false;
      }
    },
    [doneMs, messageFor],
  );

  return { run, phase, error, reset, pending: phase === 'pending' };
}
